import { useToast } from '@morressier/alembic';
import { useState } from 'react';
import { useCreateSubmissionNote } from 'src/api/commands';
import { Box, Flex } from '@chakra-ui/react';
import { TextArea } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import Divider from '../../Divider';

/**
 * This is required in each Editorial Decision Modal
 * Use the hook `useEditorialDecisionNote` to get the value, onChange and onSubmit function
 * See `EditorialDecisionModalWrapper` and `decisionModalHelper`
 * Your editorial decision modal will throw an error if this is not present in the modal
 */

export const EditorialNote = ({
  value,
  onChange
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <Box width="100%" marginBottom={4}>
        {Divider}
      </Box>

      <FieldLabel
        label="Note to Editorial Team"
        subText="This will be stored as a decision note in the notes tab for future reference."
        isRequired={false}
      />
      <TextArea
        value={value}
        minHeight={100}
        maxHeight={600}
        placeholder="Enter a message for the editorial team"
        onChange={e => onChange(e.target.value)}
      />
    </Flex>
  );
};

export const useEditorialDecisionNote = ({
  submissionId,
  submissionContextId,
  organizationId
}: {
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
}) => {
  const [editorNote, setEditorNote] = useState('');
  const toast = useToast();

  const createSubmissionNoteMutation = useCreateSubmissionNote({
    submissionId,
    submissionContextId,
    queryKey: ['submission-notes', submissionId],
    organizationId
  });

  const submitEditorNote = async (noteContent?: string) => {
    const content = noteContent ?? editorNote;
    if (content && content.trim()) {
      try {
        const response = await createSubmissionNoteMutation.mutateAsync({
          title: 'Untitled',
          content,
          mentions: [],
          category: 'Decision'
        });

        if (response.isSuccess) {
          toast({
            variant: 'success',
            title: 'Response to Editorial Team Saved',
            description:
              'Your response was stored in the notes tab for future reference.'
          });
        } else {
          toast({
            variant: 'error',
            title: 'Error',
            description:
              'There was an error saving your editorial decision note'
          });
        }
      } catch (error) {
        toast({
          variant: 'error',
          title: 'Error',
          description: 'There was an error saving your editorial decision note'
        });
      }
    }
  };

  return {
    editorNote,
    setEditorNote,
    submitEditorNote
  };
};
